import { Location } from '../../commonInfra/utils/Location';

import { BindUI } from '@tableau/react-mvvm';
import { ContentType, OmniBoxItemType } from '../models/Types';
import { IKeyboardNavigableItem, QuickSearchModule } from './QuickSearchModule';
import { HostComponentOptions, OmniboxConfigurationService } from '../OmniboxConfigurationService';
import { SearchService } from '../services/SearchService';
import { FeatureFlag, FeatureFlagService } from '../services/FeatureFlagService';
import { searchTelemetry } from '../components/SearchTelemetry';


export const CommandSuggestionImpressionType = 'commandSuggestion';

export interface CommandSuggestionItem extends IKeyboardNavigableItem {
    command: string;
    deeplinkId: string;
    icon: string;
    url: string;
}

export class CommandSuggestionViewModel extends QuickSearchModule {
    @BindUI(QuickSearchModule.buildKeyboardNavigationPath) public commandSuggestionItems: CommandSuggestionItem[];
    @BindUI() public currentKeyboardNavigableItem?: IKeyboardNavigableItem;

    public maxCommandSuggestionCount: number;

    // clearDataOnMount: determines whether command suggestions should be cleared after mounting component.
    // Since EmptyStateContentSuggestion only queries for suggestions once, we should never clear its suggestions.
    protected clearDataOnMount = true;

    public isEnabled(): boolean {
        return FeatureFlagService.instance.getValue(FeatureFlag.beaconSearchEnabled)
            && OmniboxConfigurationService.instance.configuration.hostComponent === HostComponentOptions.VPC;
    }

    public initialize(): void {
        if(this.isEnabled()) {
            // use the same maximum command suggestions as contentSuggestionMaxCount
            this.maxCommandSuggestionCount = OmniboxConfigurationService.instance.configuration.contentSuggestionMaxCount;
        }
    }

    public processSearchTerm(searchTerm: string): void {
        if(this.isEnabled()) {
            if (!this.isUnmounted) {
                searchTelemetry.sendSearchImpressionEvent(searchTerm, CommandSuggestionImpressionType);
            }
            if (searchTerm) {
                const searchResults = SearchService.instance.getCommandSuggestions(searchTerm);
                if(searchResults)
                {
                    searchResults.forEach(item => {
                        item.ownerModule = this;
                        item.getIdentifier = () => item.deeplinkId;
                    });
                }
                this.commandSuggestionItems =  searchResults;
            } else {
                this.commandSuggestionItems = [];
            }
        }
    }

    public onMount(): void {
        if (this.clearDataOnMount) {
            this.commandSuggestionItems = null;
        }
        super.onMount();
    }

    public selectItemByKeyboard(): void {
        const currentItem = this.currentKeyboardNavigableItem as CommandSuggestionItem;
        this.onItemClicked(currentItem, this.commandSuggestionItems.indexOf(currentItem));
    }

    public isKeyboardNavigable() {
        return !this.isUnmounted && this.commandSuggestionItems && this.commandSuggestionItems.length > 0;
    }

    public buildKeyboardNavigationPathInOwnModule() {
        if (this.isKeyboardNavigable()) {
            this.getFirstKeyboardNavigableItem().up = this.previousKeyboardNavigableModule.getLastKeyboardNavigableItem();

            for (let i = 0; i < this.commandSuggestionItems.length - 1; i++) {
                const currentItem = this.commandSuggestionItems[i];
                const nextItem = this.commandSuggestionItems[i + 1];
                this.linkTwoKeyboardNavigableItems(currentItem, nextItem, true);
            }

            this.getLastKeyboardNavigableItem().down = this.nextKeyboardNavigableModule.getFirstKeyboardNavigableItem();
        }
    }

    public getFirstKeyboardNavigableItem(): IKeyboardNavigableItem {
        return this.commandSuggestionItems[0];
    }

    public getLastKeyboardNavigableItem(): IKeyboardNavigableItem {
        return this.commandSuggestionItems[this.commandSuggestionItems.length - 1];
    }

    public onItemClicked(item: CommandSuggestionItem, index: number) {
        this.omniboxViewModel.onResultItemSelected(OmniBoxItemType.commandSuggestionItem, this.commandSuggestionItems.length, index, item.deeplinkId);

        // use the same gating for command suggestion as content suggestion
        if (!OmniboxConfigurationService.instance.configuration.onContentSuggestionItemClicked) {
            Location.instance.setUrl(item.url);
        } else {
            OmniboxConfigurationService.instance.configuration.onContentSuggestionItemClicked(ContentType.workbook, {} as any);
        }
    }
}
